import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Circle, Popup, useMap } from "react-leaflet";
import { getSafetyPoints } from "@/lib/api";
import { motion } from "framer-motion";
import { Crosshair, Navigation, Pause, Play } from "lucide-react";

const TYPES = {
  safe: { color: "#34C759", label: "Safe place" },
  hospital: { color: "#00E5FF", label: "Hospital" },
  help: { color: "#CCFF00", label: "Help point" },
  accessible: { color: "#B084FF", label: "Accessible place" },
  alert: { color: "#FF3B30", label: "Alert" },
};

function FollowController({ center, follow }) {
  const map = useMap();
  useEffect(() => {
    if (follow && center) {
      map.setView(center, map.getZoom(), { animate: true });
    }
  }, [center, follow, map]);
  return null;
}

function ImperativeHandles({ mapRef }) {
  const map = useMap();
  useEffect(() => {
    if (mapRef) mapRef.current = map;
  }, [map, mapRef]);
  return null;
}

export function SafetyMap() {
  const [center, setCenter] = useState([12.9716, 77.5946]);
  const [live, setLive] = useState(null); // {lat,lng,accuracy,heading,speed,ts}
  const [follow, setFollow] = useState(true);
  const [watching, setWatching] = useState(false);
  const [status, setStatus] = useState("Requesting location…");
  const [points, setPoints] = useState([]);
  const [filter, setFilter] = useState("all");
  const watchId = useRef(null);
  const mapRef = useRef(null);
  const initialisedFromLive = useRef(false);

  const startWatch = () => {
    if (!("geolocation" in navigator)) {
      setStatus("Geolocation not supported");
      return;
    }
    if (watchId.current != null) return;
    setStatus("Locating…");
    watchId.current = navigator.geolocation.watchPosition(
      (pos) => {
        const p = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy || 0,
          heading: pos.coords.heading,
          speed: pos.coords.speed,
          ts: pos.timestamp,
        };
        setLive(p);
        setStatus("Live tracking");
        if (!initialisedFromLive.current) {
          initialisedFromLive.current = true;
          setCenter([p.lat, p.lng]);
        } else if (follow) {
          setCenter([p.lat, p.lng]);
        }
      },
      (err) => {
        setStatus(err.code === 1 ? "Permission denied — showing demo" : "Location unavailable — demo mode");
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 },
    );
    setWatching(true);
  };

  const stopWatch = () => {
    if (watchId.current != null) {
      navigator.geolocation.clearWatch(watchId.current);
      watchId.current = null;
    }
    setWatching(false);
  };

  useEffect(() => {
    startWatch();
    return () => stopWatch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getSafetyPoints(center[0], center[1]).then(setPoints).catch(() => {});
  }, [center]);

  const filtered = useMemo(
    () => (filter === "all" ? points : points.filter((p) => p.type === filter)),
    [filter, points],
  );

  const recenterOnMe = () => {
    if (live && mapRef.current) {
      setFollow(true);
      mapRef.current.setView([live.lat, live.lng], 16, { animate: true });
    }
  };

  const speedKmh = live?.speed != null && !Number.isNaN(live.speed) ? Math.max(0, live.speed) * 3.6 : null;

  return (
    <section id="map" className="relative py-20 md:py-28 border-t border-white/5">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3 mb-4">
          <span className="font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-cyan)]">
            STEP 09 // COMMUNITY SAFETY MAP · LIVE
          </span>
          <span className="h-px w-14 bg-[color:var(--lg-line-strong)]" />
        </div>
        <h2 className="font-display text-3xl sm:text-5xl font-extrabold uppercase tracking-tight max-w-3xl">
          Live location + nearby <span className="text-lime-accent">safety infrastructure</span>.
        </h2>

        <div className="mt-6 flex flex-wrap items-center gap-2">
          <button onClick={() => setFilter("all")} className={`btn-tactile ${filter === "all" ? "primary" : ""}`} data-testid="map-filter-all">
            All
          </button>
          {Object.entries(TYPES).map(([k, v]) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              className={`btn-tactile ${filter === k ? "primary" : ""}`}
              data-testid={`map-filter-${k}`}
            >
              <span className="inline-block h-2 w-2 rounded-full mr-1" style={{ background: v.color }} />
              {v.label}
            </button>
          ))}

          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => setFollow((f) => !f)}
              className={`btn-tactile ${follow ? "primary" : ""}`}
              data-testid="map-follow-toggle"
              aria-pressed={follow}
              title="Keep map centred on my location"
            >
              <Navigation size={14} /> {follow ? "Following" : "Follow me"}
            </button>
            <button
              onClick={watching ? stopWatch : startWatch}
              className={`btn-tactile ${watching ? "" : "primary"}`}
              data-testid="map-live-toggle"
            >
              {watching ? <Pause size={14} /> : <Play size={14} />} {watching ? "Pause live" : "Resume live"}
            </button>
            <button onClick={recenterOnMe} className="btn-tactile" data-testid="map-recenter" title="Recenter on me">
              <Crosshair size={14} />
            </button>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-4 font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-text-3)]">
          <span className="flex items-center gap-2" data-testid="map-status">
            <span
              className="h-2 w-2 rounded-full"
              style={{
                background: watching && live ? "var(--lg-lime)" : "var(--lg-amber)",
                boxShadow: watching && live ? "0 0 12px var(--lg-lime)" : "none",
              }}
            />
            {status.toUpperCase()}
          </span>
          {live && (
            <>
              <span data-testid="map-coords">
                LAT {live.lat.toFixed(5)} · LNG {live.lng.toFixed(5)}
              </span>
              {live.accuracy > 0 && <span>±{Math.round(live.accuracy)} m</span>}
              {speedKmh != null && speedKmh > 0.5 && <span>{speedKmh.toFixed(1)} km/h</span>}
            </>
          )}
          <span className="ml-auto">{filtered.length} POINTS</span>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-4 h-[420px] md:h-[560px] rounded-md overflow-hidden border border-white/10 relative"
          data-testid="safety-map"
        >
          <MapContainer center={center} zoom={15} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }}>
            <TileLayer
              attribution='&copy; <a href="https://openstreetmap.org">OpenStreetMap</a>'
              url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <ImperativeHandles mapRef={mapRef} />
            <FollowController center={center} follow={follow} />

            {live && (
              <>
                <Circle
                  center={[live.lat, live.lng]}
                  radius={Math.max(15, live.accuracy || 25)}
                  pathOptions={{ color: "#CCFF00", weight: 1, fillColor: "#CCFF00", fillOpacity: 0.08 }}
                />
                <CircleMarker
                  center={[live.lat, live.lng]}
                  radius={8}
                  pathOptions={{ color: "#0b0f16", weight: 2, fillColor: "#CCFF00", fillOpacity: 1 }}
                  data-testid="map-you-marker"
                >
                  <Popup>
                    <div style={{ fontWeight: 700, color: "#CCFF00" }}>YOU (LIVE)</div>
                    <div>{`${live.lat.toFixed(5)}, ${live.lng.toFixed(5)}`}</div>
                    {live.accuracy > 0 && <div>Accuracy ±{Math.round(live.accuracy)}m</div>}
                  </Popup>
                </CircleMarker>
              </>
            )}

            {!live && (
              <CircleMarker
                center={center}
                radius={9}
                pathOptions={{ color: "#00E5FF", fillColor: "#00E5FF", fillOpacity: 0.85 }}
              >
                <Popup>Demo center (awaiting live location)</Popup>
              </CircleMarker>
            )}

            {filtered.map((p) => {
              const t = TYPES[p.type] || TYPES.help;
              return (
                <CircleMarker
                  key={p.id}
                  center={[p.lat, p.lng]}
                  radius={9}
                  pathOptions={{ color: t.color, fillColor: t.color, fillOpacity: 0.85, weight: 2 }}
                >
                  <Popup>
                    <div>
                      <div style={{ fontWeight: 700, textTransform: "uppercase", color: t.color }}>{t.label}</div>
                      <div style={{ marginTop: 4 }}>{p.label}</div>
                    </div>
                  </Popup>
                </CircleMarker>
              );
            })}
          </MapContainer>
        </motion.div>

        <p className="mt-3 font-mono-tac text-[10px] tracking-[0.28em] text-[color:var(--lg-text-3)]">
          LIVE TRACKING USES YOUR BROWSER&apos;S GEOLOCATION API. LIFEGRID DOES NOT UPLOAD YOUR COORDINATES.
          SAFETY POINTS ARE SIMULATED AROUND YOUR APPROXIMATE POSITION.
        </p>
      </div>
    </section>
  );
}
